import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Lock, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky banner displayed on every admin route when the company's subscription
 * is either not started or has lapsed. super_admin sees a different (info-only)
 * banner when they're impersonating a tenant.
 *
 * Two banners, two purposes:
 *   1. Trial-expired / no-subscription warning (admin) — yellow/red, links to
 *      /admin/billing. Premium features (employee creation, payroll export,
 *      reminders) are gated server-side with HTTP 402.
 *   2. Tenant impersonation indicator (super_admin) — blue, shows the active
 *      tenant + a "Switch back" button that clears the cookie.
 */
export default function SubscriptionBanner() {
  const { user, subscription, impersonation, reloadImpersonation } = useAuth();
  const location = useLocation();

  if (!user) return null;
  if (location.pathname.startsWith("/admin/billing")) return null; // don't nag on the billing page itself

  const role = user.role;
  const isAdmin = role === "admin" || role === "super_admin";
  if (!isAdmin) return null;

  // --- super_admin impersonation banner ---
  if (role === "super_admin" && impersonation?.active) {
    const clearTenant = async () => {
      try {
        const { api } = await import("@/lib/api");
        await api.post("/super/clear-tenant");
        await reloadImpersonation();
        window.location.reload();
      } catch (err) {
        const { toast } = await import("sonner");
        toast.error("Failed to clear tenant override: " + err.message);
      }
    };
    return (
      <div
        className="bg-indigo-600 text-white px-4 py-2 flex items-center gap-3 text-sm"
        data-testid="impersonation-banner"
      >
        <Sparkles className="h-4 w-4 shrink-0" />
        <span className="flex-1">
          <strong className="font-bold">Viewing tenant:</strong>{" "}
          <span data-testid="impersonation-company-name">{impersonation.name || impersonation.company_id}</span>
        </span>
        <Button
          size="sm"
          variant="secondary"
          className="h-7 bg-white/10 hover:bg-white/20 text-white border-0"
          onClick={clearTenant}
          data-testid="clear-tenant-btn"
        >
          <X className="h-3.5 w-3.5 mr-1" /> Switch back
        </Button>
      </div>
    );
  }

  // --- admin subscription banner ---
  if (role !== "admin") return null;
  if (!subscription) return null;
  if (subscription.is_active) {
    // Comped tenants never see trial-ending nags.
    if (subscription.comped) return null;
    // Active or in-trial — show a slim heads-up when trial ends within 3 days.
    const te = subscription.trial_ends_at;
    if (subscription.status === "trialing" && te) {
      const daysLeft = Math.ceil((new Date(te).getTime() - Date.now()) / 86400000);
      if (daysLeft <= 3) {
        return (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-3 text-sm text-amber-900"
            data-testid="trial-ending-banner"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              Your free trial ends in <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong>. Choose a plan to keep premium features.
            </span>
            <Link to="/admin/billing">
              <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700 text-white" data-testid="banner-cta-billing">
                Choose a plan
              </Button>
            </Link>
          </div>
        );
      }
    }
    return null;
  }

  // Locked: trial ended / canceled / never started
  const labelByStatus = {
    none: "No active subscription",
    canceled_at_period_end: "Subscription canceled — premium features locked at period end",
    expired: "Trial expired — premium features are locked",
    trialing: "Your trial has ended",
    active: "", // shouldn't happen because is_active above
    unknown: "Subscription status unavailable",
  };
  const label = labelByStatus[subscription.status] || "Premium features are locked";

  return (
    <div
      className="bg-red-50 border-b border-red-300 px-4 py-3 flex items-start gap-3 text-sm text-red-900"
      data-testid="subscription-locked-banner"
    >
      <Lock className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-bold" data-testid="subscription-locked-title">{label}</p>
        <p className="text-xs text-red-700 mt-0.5">
          The app is locked until you choose a plan. Add a card on file to unlock the dashboard, employee management, payroll, and reports.
        </p>
      </div>
      <Link to="/admin/billing">
        <Button size="sm" className="h-9 bg-red-600 hover:bg-red-700 text-white" data-testid="banner-cta-billing">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Choose a plan
        </Button>
      </Link>
    </div>
  );
}
